type Orientation = "I" | "N";

interface FontSpec {
  /** Familia de fuente ZPL: '0', 'A', 'B', etc. */
  family: string;
  /** Altura de la fuente (dots) */
  h: number;
  /** Ancho de la fuente (dots) */
  w: number;
}

interface ColumnsOptions {
  gap?: number;
  xs?: number[];
  grid?: {
    count?: number;
    left?: number;
    right?: number;
    gutter?: number;
  };
  useFB?: boolean;
}

type ColAlign = "L" | "C" | "R";

interface ColumnSpec {
  /** Texto de la celda */
  text: string | number;
  /** X absoluto (si lo querés fijar a mano). Si lo omitís, se calcula por grilla. */
  x?: number;
  /** Ancho de bloque (para ^FB). Si lo omitís y usás grilla, se infiere. */
  width?: number;
  /** Alineación del texto dentro del bloque ^FB (L, C, R). */
  align?: ColAlign;
  /** Fuente específica para esta celda (fallback: fuente actual del builder). */
  font?: FontSpec;
}

interface BuilderOptions {
  /** ^PW: ancho del label en dots */
  width?: number;
  /** ^LL: largo del label en dots */
  length?: number;
  /** ^PO: orientación ('I' invertida como tu ejemplo, o 'N') */
  orientation?: Orientation;
  /** Y inicial del cursor (desde donde empieza a “bajar” el texto) */
  startY?: number;
  /** Fuente por defecto (equivalente a ^A0I,20,19 en tu ejemplo) */
  defaultFont?: FontSpec;
  /** Separación vertical entre líneas (dots) */
  lineGap?: number;
  /** X para “labels” (columna izquierda de cada línea) */
  labelX?: number;
  /** X para “values” (columna derecha de cada línea) */
  valueX?: number;
  /** X izquierda para líneas horizontales (^FO) */
  leftX?: number;
  /** Ancho por defecto para ^GB (líneas horizontales) */
  maxWidthForGB?: number;
}

function isArguments(x: unknown): x is IArguments {
  return Object.prototype.toString.call(x) === "[object Arguments]";
}

export class ZPLBuilder {
  private readonly cfg: Required<BuilderOptions>;
  private cursorY: number;
  private font: FontSpec;
  private readonly buf: string[] = [];
  private opened = false;

  constructor(opts: BuilderOptions = {}) {
    const defaults: Required<BuilderOptions> = {
      width: 400,
      length: 1119,
      orientation: "I",
      startY: 1085,
      defaultFont: { family: "0", h: 20, w: 19 },
      lineGap: 28,
      labelX: 388,
      valueX: 230,
      leftX: 22,
      maxWidthForGB: 360,
    };

    this.cfg = { ...defaults, ...opts };
    // deep merge para defaultFont si se pasó parcialmente
    this.cfg.defaultFont = { ...defaults.defaultFont, ...(opts.defaultFont ?? {}) };
    this.cursorY = this.cfg.startY;
    this.font = { ...this.cfg.defaultFont };
  }

  // ---------- internos ----------
  private openIfNeeded(): void {
    if (this.opened) return;
    this.opened = true;
    const { width, length, orientation } = this.cfg;

    // Header como en tu ejemplo
    this.buf.push("^XA");
    this.buf.push("^CI28 ^MMT");
    this.buf.push(`^PW${width} ^LL${length} ^LS0`);
    this.buf.push(`^PO${orientation}`);
  }

  private close(): void {
    // Footer doble como tu ejemplo
    this.buf.push("^XZ");
    this.buf.push("^XA");
    this.buf.push("^PON");
    this.buf.push("^XZ");
  }

  private advance(lines = 1, overrideGap?: number): void {
    const step = overrideGap ?? this.cfg.lineGap;

    if (this.cfg.orientation === "I") {
      this.cursorY -= step * lines;
    } else {
      this.cursorY += step * lines;
    }
  }

  // ---------- API pública ----------
  /** Cambia la fuente actual (solo lo que pases). */
  public setFont(spec: Partial<FontSpec> = {}): this {
    this.font = { ...this.font, ...spec };

    return this;
  }

  /** Deja un espacio vertical específico (en dots). */
  public gap(px: number): this {
    this.openIfNeeded();
    this.advance(1, px);

    return this;
  }

  /** Escapa mínimamente texto para ^FH (escapa ^ y ~). */
  public static zplEscape(text: unknown): string {
    if (text == null) return "";
    const s = String(text);

    return s.replace(/\^/g, "_5E").replace(/~/g, "_7E");
  }

  /** Inserta ZPL crudo tal cual. */
  public raw(zpl: string): this {
    this.openIfNeeded();
    this.buf.push(zpl);

    return this;
  }

  /** Título grande (por defecto centrado aprox como tu ejemplo). */
  public title(
    text: string,
    opts: {
      x?: number;
      font?: FontSpec;
      gap?: number;
    } = {},
  ): this {
    this.openIfNeeded();
    const x = opts.x ?? 330;
    const font = opts.font ?? { family: this.font.family, h: 28, w: 27 };
    const t = ZPLBuilder.zplEscape(text);

    this.buf.push(
      `^FT${x},${this.cursorY}^A${font.family}${this.cfg.orientation},${font.h},${font.w}^FH^FD${t}^FS`,
    );
    this.advance(1, opts.gap);

    return this;
  }

  /** Línea con “Label:” + “Valor” en dos columnas. */
  public line(
    label: string,
    value: string | number,
    opts: {
      labelX?: number;
      valueX?: number;
      font?: FontSpec;
      gap?: number;
    } = {},
  ): this {
    return this.columns(
      [
        { text: label, x: opts.labelX ?? this.cfg.labelX, font: opts.font },
        { text: value, x: opts.valueX ?? this.cfg.valueX, font: opts.font },
      ],
      { xs: [388, 310], gap: 26, useFB: false },
    );
  }

  /** Subtítulo/encabezado de sección. */
  public section(
    titleText: string,
    opts: {
      x?: number;
      font?: FontSpec;
      gap?: number;
    } = {},
  ): this {
    this.openIfNeeded();
    const x = opts.x ?? 286;
    const font = opts.font ?? { family: this.font.family, h: 11, w: 12 };
    const t = ZPLBuilder.zplEscape(titleText);

    this.buf.push(
      `^FT${x},${this.cursorY}^A${font.family}${this.cfg.orientation},${font.h},${font.w}^FH^FD${t}^FS`,
    );
    this.advance(1, opts.gap ?? 30);

    return this;
  }

  public columns(
    cols: ColumnSpec[] | unknown, // aceptamos 'any' para poder sanear llamada tipo tupla
    options?: ColumnsOptions,
  ): this {
    this.openIfNeeded();

    // --- Saneamiento de llamadas erróneas ---
    // Caso: columns([cols, opts]) → un solo arg que es una tupla/array
    if (
      Array.isArray(cols) &&
      cols.length === 2 &&
      Array.isArray(cols[0]) &&
      options === undefined
    ) {
      const [c, o] = cols as [ColumnSpec[], ColumnsOptions?];

      cols = c;
      options = o;
    }
    // Caso: columns(cols, arguments) (pasaron el objeto 'arguments' sin querer)
    if (isArguments(options)) {
      const arr = Array.from(options) as [ColumnSpec[], ColumnsOptions?];

      if (Array.isArray(arr[0])) {
        cols = arr[0];
        options = arr[1];
      } else {
        options = undefined;
      }
    }

    const list = cols as ColumnSpec[];
    const opts: ColumnsOptions = options ?? {};

    if (list.length === 0) {
      this.advance(1, opts.gap);

      return this;
    }

    // --- Cálculo de Xs (igual que antes) ---
    let xs: number[] | undefined = opts.xs?.slice();
    const grid = opts.grid ?? {};
    const count = grid.count ?? list.length;

    if (!xs) {
      const anyManual = list.some((c) => typeof c.x === "number");

      if (anyManual) {
        xs = list.map((c) => (typeof c.x === "number" ? c.x : NaN));
        const left = grid.left ?? this.cfg.valueX;
        const right = grid.right ?? this.cfg.width - 40;
        const gutter = grid.gutter ?? 20;
        const usable = Math.max(0, right - left);
        const colWidth = Math.max(1, Math.floor((usable - gutter * (count - 1)) / count));

        for (let i = 0; i < xs.length; i++) {
          if (Number.isNaN(xs[i])) xs[i] = left + i * (colWidth + gutter);
        }
        // setea width por defecto si faltó
        for (let i = 0; i < list.length; i++) {
          if (list[i]?.width == null) {
            const element = list[i];

            if (element) element.width = colWidth;
          }
        }
      } else {
        const left = grid.left ?? this.cfg.valueX;
        const right = grid.right ?? this.cfg.width - 40;
        const gutter = grid.gutter ?? 20;
        const usable = Math.max(0, right - left);
        const colWidth = Math.max(1, Math.floor((usable - gutter * (count - 1)) / count));

        xs = Array.from({ length: count }, (_, i) => left + i * (colWidth + gutter));
        for (let i = 0; i < list.length; i++) {
          if (list[i]?.width == null) {
            const element = list[i];

            if (element) element.width = colWidth;
          }
        }
      }
    }

    // --- Render de celdas ---
    for (let i = 0; i < list.length; i++) {
      const c = list[i];
      const x = typeof c?.x === "number" ? c.x : xs[i] ?? this.cfg.valueX;
      const font = c?.font ?? this.font;
      const txt = ZPLBuilder.zplEscape(c?.text);
      const useFB = (opts.useFB ?? true) || !!c?.align || !!c?.width;

      if (useFB) {
        const width = Math.max(1, c?.width ?? 120);
        const align = c?.align ?? "L"; // L/C/R

        this.buf.push(
          `^FT${x},${this.cursorY}^A${font.family}${this.cfg.orientation},${font.h},${font.w}` +
            `^FB${width},1,${align},0,0^FH^FD${txt}^FS`,
        );
      } else {
        this.buf.push(
          `^FT${x},${this.cursorY}^A${font.family}${this.cfg.orientation},${font.h},${font.w}^FH^FD${txt}^FS`,
        );
      }
    }

    this.advance(1, opts.gap);

    return this;
  }

  /** Línea horizontal (^GB) tipo separador. */
  public hr(opts: { x?: number; width?: number; thickness?: number; gap?: number } = {}): this {
    this.openIfNeeded();
    const x = opts.x ?? this.cfg.leftX;
    const width = opts.width ?? this.cfg.maxWidthForGB;
    const thickness = opts.thickness ?? 2;
    const gap = opts.gap ?? 28;

    this.buf.push(`^FO${x},${this.cursorY}^GB${width},0,${thickness}^FS`);
    this.advance(1, gap);

    return this;
  }

  /** Devuelve el ZPL completo (agrega footer como en tu ejemplo). */
  public build(): string {
    if (!this.opened) this.openIfNeeded();
    this.close();

    return this.buf.join("\n");
  }
}
